import React, { useEffect, useMemo, useRef, useState } from 'react';
import Copy from '@spectrum-icons/workflow/Copy';
import Checkmark from '@spectrum-icons/workflow/Checkmark';
import Search from '@spectrum-icons/workflow/Search';
import Close from '@spectrum-icons/workflow/Close';
import Star from '@spectrum-icons/workflow/Star';
import { bestFitsFor, inferTextType } from '../../lib/textScoring';

// Width threshold above which a chip spans the full grid row instead of
// sharing a column with a peer. Keeps short nav-style chips dense while
// paragraph-length chips stay readable.
const WIDE_CHIP_THRESHOLD = 60;

export default function TextsTab({
  texts,
  onImportClick,
  selection,
  selectedText,
  canReplace,
  replacingTextId,
  onReplace,
  onReplaceAll,
  replaceAllStatus = { phase: 'idle', progress: { done: 0, total: 0 }, error: '' },
}) {
  const categories = Object.keys(texts);
  const [expanded, setExpanded] = useState(() => new Set(['banner']));
  const [query, setQuery] = useState('');
  // Stable id of the chip showing the "Copied" flash, or '' when none.
  const [copiedId, setCopiedId] = useState('');
  // Stable id of the chip showing the post-replace green flash, or '' when none.
  const [replacedId, setReplacedId] = useState('');
  // Hold timers so re-clicks before the flash ends restart cleanly instead of
  // leaving a dangling timeout that wipes the new flash early.
  const copyTimerRef = useRef(null);
  const replacedTimerRef = useRef(null);

  useEffect(() => () => {
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    if (replacedTimerRef.current) clearTimeout(replacedTimerRef.current);
  }, []);

  const toggle = (cat) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  };

  // Iframe-friendly copy. The Universal Editor host iframe doesn't grant
  // `clipboard-write` Permissions Policy, so navigator.clipboard.writeText
  // throws NotAllowedError. The legacy textarea + execCommand path is gated
  // by user activation (which we have inside this click handler), not by
  // Permissions Policy, so it still works there. Try the modern API first
  // and fall through to the legacy one on any failure.
  const legacyCopy = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    const sel = document.getSelection();
    const preserved = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
    ta.select();
    ta.setSelectionRange(0, text.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch (_) {
      ok = false;
    }
    document.body.removeChild(ta);
    if (preserved && sel) {
      sel.removeAllRanges();
      sel.addRange(preserved);
    }
    return ok;
  };

  const handleCopy = async (id, text, e) => {
    if (e) e.stopPropagation();
    let ok = false;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        ok = true;
      } catch (_) { /* fall through */ }
    }
    if (!ok) ok = legacyCopy(text);
    if (!ok) {
      // eslint-disable-next-line no-console
      console.warn('[DemoPilot] clipboard copy failed (both modern and legacy paths)');
      return;
    }
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    setCopiedId(id);
    copyTimerRef.current = setTimeout(() => {
      setCopiedId('');
      copyTimerRef.current = null;
    }, 1100);
  };

  const hasSelection = !!selection?.cleanResource;
  const replaceEnabled = canReplace && hasSelection && !replacingTextId;

  const replaceDisabledReason = () => {
    if (!canReplace) return 'Replace actions not deployed yet';
    if (!hasSelection) return 'Select a text node in the canvas first';
    if (replacingTextId) return 'Another replace is in progress';
    return '';
  };

  const handleBubbleClick = async (id, text) => {
    if (!replaceEnabled || typeof onReplace !== 'function') return;
    const ok = await onReplace(id, text);
    if (!ok) return;
    if (replacedTimerRef.current) clearTimeout(replacedTimerRef.current);
    setReplacedId(id);
    replacedTimerRef.current = setTimeout(() => {
      setReplacedId('');
      replacedTimerRef.current = null;
    }, 1200);
  };

  // Flatten the per-category texts into a single ranked list — needed for
  // bestFitsFor scoring AND for "total texts" counts.
  const allChips = useMemo(() => {
    const out = [];
    for (const cat of categories) {
      const arr = texts[cat] || [];
      for (let i = 0; i < arr.length; i++) {
        out.push({ id: `${cat}:${i}`, text: arr[i], category: cat });
      }
    }
    return out;
  }, [texts]);

  const totalCount = allChips.length;

  // Best fits — sourced from the full catalog, then filtered by the search
  // query (if any). When there's no selection or no scrape data, this is [].
  const bestFits = useMemo(
    () => bestFitsFor(selectedText, allChips),
    [selectedText, allChips]
  );

  const q = query.trim().toLowerCase();
  const matchesQuery = (text) => !q || text.toLowerCase().includes(q);

  const filteredBestFits = useMemo(
    () => bestFits.filter((c) => matchesQuery(c.text)),
    [bestFits, q]
  );

  const selectionType = inferTextType(selectedText);

  // Build the per-category render data — only categories with at least one
  // visible item are kept. Used both to render the accordion and to decide
  // "no results" copy.
  const visibleCategories = useMemo(() => {
    const out = [];
    for (const cat of categories) {
      const arr = texts[cat] || [];
      const items = arr
        .map((t, i) => ({ id: `${cat}:${i}`, text: t, category: cat }))
        .filter((c) => matchesQuery(c.text));
      if (items.length > 0) out.push({ cat, items, totalInCat: arr.length });
    }
    return out;
  }, [texts, q]);

  const hasAny = totalCount > 0;
  const disabledReason = replaceDisabledReason();

  const renderChip = ({ id, text }) => {
    const isCopied = copiedId === id;
    const isReplaced = replacedId === id;
    const isReplacing = replacingTextId === id;
    const isWide = text.length > WIDE_CHIP_THRESHOLD;
    const cls = [
      'dp-text-chip',
      isCopied && 'is-copied',
      isReplaced && 'is-replaced',
      isReplacing && 'is-replacing',
      !replaceEnabled && 'is-disabled',
      isWide && 'is-wide',
    ].filter(Boolean).join(' ');
    const chipTitle = !replaceEnabled
      ? disabledReason
      : 'Click to replace the selected text in the canvas';
    return (
      <div
        key={id}
        className={cls}
        role="button"
        tabIndex={replaceEnabled ? 0 : -1}
        title={chipTitle}
        aria-disabled={!replaceEnabled}
        aria-busy={isReplacing}
        onClick={() => handleBubbleClick(id, text)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleBubbleClick(id, text);
          }
        }}
      >
        <span className="dp-text-chip-content">{text}</span>
        <button
          type="button"
          className="dp-text-chip-copy"
          title={isCopied ? 'Copied' : 'Copy to clipboard'}
          aria-label={isCopied ? 'Copied' : 'Copy to clipboard'}
          onClick={(e) => handleCopy(id, text, e)}
        >
          {isCopied
            ? <Checkmark size="XS" aria-hidden />
            : <Copy size="XS" aria-hidden />}
        </button>
        {isReplacing && (
          <span className="dp-text-chip-overlay" aria-hidden>
            <span className="dp-spinner" />
            <span>Replacing…</span>
          </span>
        )}
      </div>
    );
  };

  const replaceAllBusy = replaceAllStatus.phase !== 'idle' && replaceAllStatus.phase !== 'done' && replaceAllStatus.phase !== 'error';
  const replaceAllEnabled = hasAny && typeof onReplaceAll === 'function' && !replaceAllBusy;

  const replaceAllLabel = () => {
    const { phase, progress } = replaceAllStatus;
    if (phase === 'collecting') return `Analyzing page… (${progress.done}/${progress.total})`;
    if (phase === 'generating') return 'Generating with AI…';
    if (phase === 'applying') return `Applying… (${progress.done}/${progress.total})`;
    if (phase === 'done') return `Done! (${progress.done} replaced)`;
    return 'Replace All Text';
  };

  return (
    <div className="dp-tab-content">
      <button className="dp-btn dp-btn-primary dp-btn-full" onClick={onImportClick}>
        Import Texts
      </button>

      {hasAny && (
        <button
          className={`dp-btn dp-btn-full dp-btn-ai ${replaceAllBusy ? 'is-busy' : ''} ${replaceAllStatus.phase === 'done' ? 'is-done' : ''}`}
          onClick={replaceAllEnabled ? onReplaceAll : undefined}
          disabled={!replaceAllEnabled}
        >
          {replaceAllBusy && <span className="dp-spinner dp-spinner-ai" aria-hidden />}
          <span>{replaceAllLabel()}</span>
        </button>
      )}

      {replaceAllStatus.phase === 'error' && replaceAllStatus.error && (
        <div className="dp-status is-error">
          <span>{replaceAllStatus.error}</span>
        </div>
      )}

      {hasAny && (
        <div className="dp-texts-sticky">
          <div className="dp-search">
            <span className="dp-search-icon" aria-hidden>
              <Search size="XS" />
            </span>
            <input
              type="text"
              className="dp-search-input"
              placeholder={`Search ${totalCount} text${totalCount === 1 ? '' : 's'}…`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search scraped texts"
            />
            {query && (
              <button
                type="button"
                className="dp-search-clear"
                aria-label="Clear search"
                title="Clear"
                onClick={() => setQuery('')}
              >
                <Close size="XS" aria-hidden />
              </button>
            )}
          </div>

          {selectedText && (
            <div className="dp-selection-strip" title={selectedText}>
              <span className="dp-selection-label">Selected</span>
              <span className="dp-selection-value">{selectedText}</span>
              {selectionType && (
                <span className={`dp-selection-type is-${selectionType}`}>
                  {selectionType}
                </span>
              )}
            </div>
          )}

          {!replaceEnabled && (
            <div className="dp-status dp-notice-warn">
              <span>{disabledReason}</span>
            </div>
          )}
        </div>
      )}

      {filteredBestFits.length > 0 && (
        <section className="dp-best-fits" aria-label="Best fits for current selection">
          <div className="dp-best-fits-header">
            <Star size="XS" aria-hidden />
            <span>Best fits</span>
            <span className="dp-best-fits-count">{filteredBestFits.length}</span>
          </div>
          <div className="dp-text-grid">
            {filteredBestFits.map(renderChip)}
          </div>
        </section>
      )}

      {!hasAny ? (
        <p className="dp-empty">No texts scraped yet…</p>
      ) : visibleCategories.length === 0 ? (
        <p className="dp-empty">No texts match “{query}”</p>
      ) : (
        <div className="dp-accordion">
          {visibleCategories.map(({ cat, items, totalInCat }) => {
            // During search, every category with matches stays open; the
            // manual expanded state takes over again once the query clears.
            const isOpen = q ? true : expanded.has(cat);
            return (
              <div key={cat} className="dp-accordion-item">
                <button
                  type="button"
                  className="dp-accordion-header"
                  onClick={() => !q && toggle(cat)}
                  aria-expanded={isOpen}
                  disabled={!!q}
                >
                  <span className={`dp-chevron ${isOpen ? 'is-open' : ''}`}>›</span>
                  <span className="dp-accordion-title">{cat}</span>
                  <span className="dp-accordion-count">
                    {q ? `${items.length}/${totalInCat}` : totalInCat}
                  </span>
                </button>
                {isOpen && (
                  <div className="dp-accordion-body dp-text-grid">
                    {items.map(renderChip)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
